import _isEqual from 'lodash/isEqual';

import {
  Allocations,
  FundAllocation,
  QdiaFundData,
  SimpleQdiaFundData,
  WeightedSecurities,
  WeightedSecuritiesWithAllocations,
} from './data';

export const convertToPercentDisplay = (value: number | undefined | null) => {
  return value ? `${(value / 100).toFixed(2)}%` : '\u2014';
};

export const ensureNumber = (value: string | number) => {
  return typeof value === 'string' ? parseInt(value, 10) || 0 : value;
};

export const totalAllocationWeight = (allocations: Allocations) => {
  return Object.values(allocations).reduce((acc, curr) => ensureNumber(acc) + ensureNumber(curr), 0);
};

export const portfolioIsCurrentlyQdia = (
  qdiaFund: QdiaFundData | SimpleQdiaFundData,
  weightedSecurities: WeightedSecurities | WeightedSecuritiesWithAllocations
) => {
  const qdiaSlug = qdiaFund?.slug;
  const qdiaAllocationPercent = weightedSecurities?.find(
    weightedSecurity => weightedSecurity.security?.slug === qdiaSlug
  )?.allocationPercent;
  return qdiaAllocationPercent === 100;
};

export const currentFullyAllocatedTargetDateFund = (
  weightedSecurities: WeightedSecurities | WeightedSecuritiesWithAllocations
) => {
  const fullyAllocatedFund = weightedSecurities.find(weightedSecurity => weightedSecurity.allocationPercent === 100);
  if (fullyAllocatedFund?.security?.grandparentGroupName === 'target date funds') {
    return fullyAllocatedFund;
  }
  return null;
};

export const formattedAllocationsForSubmission = (values: Allocations) => {
  return Object.entries(values).reduce((acc, [key, value]) => {
    if (ensureNumber(value) > 0) {
      acc.push({ slug: key, allocationPercent: ensureNumber(value) });
    }
    return acc;
  }, [] as FundAllocation[]);
};

export const currentPortfolioName = (weightedSecurities: WeightedSecurities | WeightedSecuritiesWithAllocations) => {
  return currentFullyAllocatedTargetDateFund(weightedSecurities) ? 'Target date fund' : 'Custom portfolio';
};

export const currentPortfolioSubName = (
  weightedSecurities: WeightedSecurities | WeightedSecuritiesWithAllocations
): string | null => {
  const targetDateFund = currentFullyAllocatedTargetDateFund(weightedSecurities);
  return targetDateFund ? targetDateFund.security.name : null;
};

export const mapSecuritiesToAllocations = (weightedSecurities: WeightedSecuritiesWithAllocations) => {
  return weightedSecurities.reduce((acc: Allocations, fund) => {
    acc[fund.security.slug] = fund.allocationPercent;
    return acc;
  }, {});
};

export const portfolioAllocationsChanged = (
  newAllocations: Allocations,
  weightedSecurities: WeightedSecuritiesWithAllocations
) => {
  const currentAllocations = mapSecuritiesToAllocations(weightedSecurities);
  return !_isEqual(currentAllocations, newAllocations);
};

export const newFullyAllocatedTargetDateFund = (
  values: Allocations,
  weightedSecurities: WeightedSecuritiesWithAllocations
) => {
  const fullyAllocatedFundSlug = Object.entries(values).find(([, value]) => value === 100)?.[0];

  if (!fullyAllocatedFundSlug) {
    return null;
  }

  const fullyAllocatedFund = weightedSecurities?.find(
    ({ security }) => security.slug === fullyAllocatedFundSlug && security.grandparentGroupName === 'target date funds'
  );

  return fullyAllocatedFund || null;
};

export const newPortfolioName = (values: Allocations, weightedSecurities: WeightedSecuritiesWithAllocations) => {
  const targetDateFund = newFullyAllocatedTargetDateFund(values, weightedSecurities);
  if (targetDateFund) {
    return 'Target date fund';
  }
  return 'Custom portfolio';
};

export const newPortfolioSubname = (
  values: Allocations,
  currentPortfolioNameVar: string,
  weightedSecurities: WeightedSecuritiesWithAllocations
) => {
  const targetDateFund = newFullyAllocatedTargetDateFund(values, weightedSecurities);
  if (targetDateFund) {
    return targetDateFund.security.name;
  }
  if (currentPortfolioNameVar === 'Custom portfolio') {
    return 'Allocations updated';
  }
  return null;
};
